/**
 * A minimal observable value.
 *
 * Every stateful service in this app exposes `getSnapshot()` / `subscribe()`.
 * The pattern is framework-neutral — it drives in-world panels exactly as well
 * as it drove React in the reference project — and it keeps services testable
 * without a world, a renderer or a DOM.
 */
export type Listener<T> = (value: T) => void;

export class Store<T> {
  private value: T;
  private readonly listeners = new Set<Listener<T>>();

  constructor(initial: T) {
    this.value = initial;
  }

  getSnapshot(): T {
    return this.value;
  }

  /**
   * Subscribe to changes. Fires immediately with the current value so late
   * subscribers do not have to wait for the next mutation to render — the same
   * replay guarantee `onPanelReady` gives, for the same reason.
   *
   * @returns an unsubscribe function.
   */
  subscribe(listener: Listener<T>): () => void {
    this.listeners.add(listener);
    listener(this.value);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** Replace the value and notify. No-op when the value is identical. */
  set(next: T): void {
    if (Object.is(next, this.value)) return;
    this.value = next;
    this.emit();
  }

  /** Shallow-merge a patch into an object value and notify. */
  patch(partial: Partial<T>): void {
    this.value = { ...this.value, ...partial };
    this.emit();
  }

  private emit(): void {
    // Copy first: a listener may unsubscribe (or subscribe) during dispatch,
    // and mutating the set mid-iteration would skip or repeat listeners.
    for (const listener of [...this.listeners]) {
      listener(this.value);
    }
  }
}
